<template>
    <div class="container-fluid flow-config-page">
        <div class="row px-3 py-4">
            <div class="col-12">
                <div class="page-toolbar">
                    <div>
                        <h2 class="page-title mb-1">Live Search Flow Configuration</h2>
                    </div>
                    <div class="d-flex gap-2">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="fetchConfiguration"
                            :disabled="isLoading || isSaving"
                        >
                            Refresh
                        </button>
                        <button
                            type="button"
                            class="btn btn-danger"
                            @click="openCreateSidebar"
                            :disabled="isLoading || isSaving"
                        >
                            Add Vendor
                        </button>
                    </div>
                </div>

                <div class="legend-trigger mt-3">
                    Legend
                    <span tabindex="0" class="legend-trigger-wrapper">
                        <font-awesome-icon :icon="['fas', 'book-open']" />
                        <div class="legend-popover">
                            <p class="legend-title">Legend</p>
                            <ul>
                                <li>
                                    <span class="status-pill status-pill--active"
                                        >Active source</span
                                    >
                                </li>
                                <li>
                                    <span class="status-pill status-pill--inactive"
                                        >Inactive source</span
                                    >
                                </li>
                                <li>
                                    <span class="mode-indicator mode-indicator--deployment">
                                        <font-awesome-icon :icon="['fas', 'plane-departure']" />
                                    </span>
                                    Deployment mode
                                </li>
                                <li>
                                    <span class="mode-indicator mode-indicator--direct">
                                        <font-awesome-icon :icon="['fas', 'plane-departure']" />
                                    </span>
                                    Direct mode
                                </li>
                            </ul>
                        </div>
                    </span>
                </div>

                <div v-if="loadError" class="alert alert-danger mt-3">
                    {{ loadError }}
                </div>

                <div class="table-card">
                    <div v-if="isLoading" class="py-5 text-center">
                        <div class="spinner-border text-danger" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>

                    <div v-else-if="sortedItems.length === 0" class="empty-state">
                        <h3>No vendor configurations found</h3>
                        <p class="text-muted mb-0">Add the first live-search vendor.</p>
                    </div>

                    <div v-else class="table-responsive">
                        <table class="table table-hover align-middle mb-0 vendor-table">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Flow Name</th>
                                    <th>Module</th>
                                    <th>Method</th>
                                    <th>Mode</th>
                                    <th class="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="vendor in sortedItems" :key="vendor.source">
                                    <td>
                                        <span
                                            class="status-pill"
                                            :class="
                                                vendor.is_active
                                                    ? 'status-pill--active'
                                                    : 'status-pill--inactive'
                                            "
                                        >
                                            {{ vendor.source }}
                                        </span>
                                    </td>
                                    <td>{{ vendor.flow_name }}</td>
                                    <td>{{ vendor.module_name }}</td>
                                    <td>{{ vendor.method_name }}</td>
                                    <td>
                                        <span
                                            class="mode-indicator"
                                            :class="
                                                vendor.use_deployment
                                                    ? 'mode-indicator--deployment'
                                                    : 'mode-indicator--direct'
                                            "
                                            :title="
                                                vendor.use_deployment
                                                    ? 'Deployment mode'
                                                    : 'Direct mode'
                                            "
                                        >
                                            <font-awesome-icon :icon="['fas', 'plane-departure']" />
                                        </span>
                                        <span class="visually-hidden">
                                            {{
                                                vendor.use_deployment
                                                    ? 'Deployment mode'
                                                    : 'Direct mode'
                                            }}
                                        </span>
                                    </td>
                                    <td class="text-end">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-secondary action-button"
                                            @click="openEditSidebar(vendor)"
                                        >
                                            <font-awesome-icon :icon="['fas', 'edit']" />
                                            <span class="ms-2">Edit</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <transition name="details-slide">
            <aside
                v-if="detailsVisible"
                class="details-sidebar"
                role="dialog"
                aria-modal="true"
                tabindex="-1"
                ref="detailsSidebar"
                @click.stop
                @keyup.esc="closeDetails"
            >
                <div class="details-sidebar__header">
                    <div>
                        <p class="details-sidebar__eyebrow">
                            {{ isCreating ? 'New Vendor' : 'Vendor Configuration' }}
                        </p>
                        <h3 class="details-sidebar__title">
                            {{ draftVendor.source || 'Untitled Vendor' }}
                        </h3>
                        <div class="details-sidebar__badges">
                            <span
                                class="status-pill"
                                :class="
                                    draftVendor.is_active
                                        ? 'status-pill--active'
                                        : 'status-pill--inactive'
                                "
                            >
                                {{ draftVendor.is_active ? 'Active' : 'Inactive' }}
                            </span>
                            <span class="status-pill status-pill--neutral">
                                {{ draftVendor.use_deployment ? 'Deployment' : 'Direct' }}
                            </span>
                        </div>
                    </div>
                    <button type="button" class="details-sidebar__close" @click="closeDetails">
                        <span aria-hidden="true">&times;</span>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>

                <div class="details-sidebar__content">
                    <div v-if="formError" class="alert alert-danger">
                        {{ formError }}
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Source</label>
                        <input
                            v-model="draftVendor.source"
                            type="text"
                            class="form-control"
                            placeholder="parts_base"
                            :disabled="!isCreating"
                        />
                        <div v-if="fieldError('source')" class="field-error">
                            {{ fieldError('source') }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Flow Name</label>
                        <input
                            v-model="draftVendor.flow_name"
                            type="text"
                            class="form-control"
                            placeholder="search_parts_base"
                            :disabled="!isCreating"
                        />
                        <div v-if="fieldError('flow_name')" class="field-error">
                            {{ fieldError('flow_name') }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Module Name</label>
                        <input
                            v-model="draftVendor.module_name"
                            type="text"
                            class="form-control"
                            placeholder="searchaction_parts_base"
                            :disabled="!isCreating"
                        />
                        <div v-if="fieldError('module_name')" class="field-error">
                            {{ fieldError('module_name') }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Method Name</label>
                        <input
                            v-model="draftVendor.method_name"
                            type="text"
                            class="form-control"
                            placeholder="search_parts_base"
                            :disabled="!isCreating"
                        />
                        <div v-if="fieldError('method_name')" class="field-error">
                            {{ fieldError('method_name') }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Deployment Name</label>
                        <input
                            v-model="draftVendor.deployment_name"
                            type="text"
                            class="form-control"
                            :disabled="!isCreating || !draftVendor.use_deployment"
                            placeholder="search_parts_base/SA-Parts_base"
                        />
                        <div v-if="fieldError('deployment_name')" class="field-error">
                            {{ fieldError('deployment_name') }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <div class="form-check form-switch">
                            <input
                                id="vendor-use-deployment"
                                v-model="draftVendor.use_deployment"
                                class="form-check-input"
                                type="checkbox"
                                :disabled="!isCreating"
                            />
                            <label class="form-check-label" for="vendor-use-deployment">
                                Use Deployment
                            </label>
                        </div>
                    </div>

                    <div class="mb-4">
                        <div class="form-check form-switch">
                            <input
                                id="vendor-is-active"
                                v-model="draftVendor.is_active"
                                class="form-check-input"
                                type="checkbox"
                            />
                            <label class="form-check-label" for="vendor-is-active"> Active </label>
                        </div>
                    </div>

                    <div class="d-grid">
                        <button
                            type="button"
                            class="btn btn-danger"
                            @click="submitVendor"
                            :disabled="isSaving"
                        >
                            <span
                                v-if="isSaving"
                                class="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            {{ isCreating ? 'Create Vendor' : 'Update Vendor' }}
                        </button>
                    </div>
                </div>
            </aside>
        </transition>

        <transition name="details-fade">
            <div v-if="detailsVisible" class="details-backdrop" @click="closeDetails"></div>
        </transition>
    </div>
</template>

<script>
    import { mapStores } from 'pinia'
    import { toast } from 'vue3-toastify'
    import flowConfigurationService from '@/services/flowConfiguration.service'
    import { useUserStore } from '@/stores/userStore'

    const createEmptyVendor = () => ({
        source: '',
        flow_name: '',
        is_active: true,
        method_name: '',
        module_name: '',
        use_deployment: true,
        deployment_name: '',
    })

    export default {
        name: 'FlowConfigurationLiveSearchPage',
        computed: {
            ...mapStores(useUserStore),
            sortedItems() {
                return [...this.items].sort((left, right) => {
                    if (left.is_active !== right.is_active) {
                        return left.is_active ? -1 : 1
                    }
                    if (left.use_deployment !== right.use_deployment) {
                        return left.use_deployment ? -1 : 1
                    }
                    return String(left.source).localeCompare(String(right.source))
                })
            },
        },
        data() {
            return {
                isLoading: false,
                isSaving: false,
                loadError: '',
                formError: '',
                items: [],
                detailsVisible: false,
                isCreating: false,
                originalSource: '',
                draftVendor: createEmptyVendor(),
                validationErrors: {},
            }
        },
        async mounted() {
            if (!this.userStore.isStaff) {
                this.$router.replace({ name: 'dashboard' })
                return
            }

            await this.fetchConfiguration()
        },
        methods: {
            normalizeVendor(vendor) {
                const normalized = {
                    source: (vendor.source || '').trim(),
                    flow_name: (vendor.flow_name || '').trim(),
                    is_active: Boolean(vendor.is_active),
                    method_name: (vendor.method_name || '').trim(),
                    module_name: (vendor.module_name || '').trim(),
                    use_deployment: Boolean(vendor.use_deployment),
                    deployment_name: (vendor.deployment_name || '').trim(),
                }

                if (!normalized.use_deployment) {
                    normalized.deployment_name = ''
                }

                return normalized
            },
            validateVendor(vendor) {
                const errors = {}
                const requiredFields = ['source', 'flow_name', 'module_name', 'method_name']

                requiredFields.forEach((field) => {
                    if (!vendor[field]) {
                        errors[field] = `${field} is required.`
                    }
                })

                const duplicate = this.items.find(
                    (item) =>
                        item.source === vendor.source &&
                        (this.isCreating || item.source !== this.originalSource)
                )
                if (duplicate) {
                    errors.source = 'source must be unique.'
                }

                if (vendor.use_deployment && !vendor.deployment_name) {
                    errors.deployment_name =
                        'deployment_name is required when use_deployment is enabled.'
                }

                return errors
            },
            fieldError(field) {
                return this.validationErrors[field] || ''
            },
            async fetchConfiguration() {
                this.isLoading = true
                this.loadError = ''

                try {
                    const response = await flowConfigurationService.fetchLiveSearchConfig()
                    this.items = response?.config || response?.data?.config || []
                } catch (error) {
                    this.loadError =
                        error?.response?.data?.error ||
                        'Failed to load live search flow configuration.'
                } finally {
                    this.isLoading = false
                }
            },
            openCreateSidebar() {
                this.isCreating = true
                this.originalSource = ''
                this.draftVendor = createEmptyVendor()
                this.validationErrors = {}
                this.formError = ''
                this.detailsVisible = true
                this.$nextTick(() => this.$refs.detailsSidebar?.focus())
            },
            openEditSidebar(vendor) {
                this.isCreating = false
                this.originalSource = vendor.source
                this.draftVendor = { ...vendor }
                this.validationErrors = {}
                this.formError = ''
                this.detailsVisible = true
                this.$nextTick(() => this.$refs.detailsSidebar?.focus())
            },
            closeDetails() {
                if (this.isSaving) return
                this.detailsVisible = false
                this.validationErrors = {}
                this.formError = ''
            },
            async submitVendor() {
                this.isSaving = true
                this.formError = ''

                const normalizedVendor = this.normalizeVendor(this.draftVendor)
                this.validationErrors = this.validateVendor(normalizedVendor)

                if (Object.keys(this.validationErrors).length) {
                    this.formError = 'Fix the validation errors before saving.'
                    this.isSaving = false
                    return
                }

                try {
                    const response = this.isCreating
                        ? await flowConfigurationService.createLiveSearchVendor(normalizedVendor)
                        : await flowConfigurationService.updateLiveSearchVendor(
                              this.originalSource,
                              normalizedVendor
                          )

                    this.items = response?.config || response?.data?.config || []
                    this.closeDetails()
                    toast.success(
                        this.isCreating
                            ? 'Vendor configuration created.'
                            : 'Vendor configuration updated.'
                    )
                } catch (error) {
                    const apiError = error?.response?.data?.error
                    this.formError = apiError || 'Failed to save vendor configuration.'

                    if (apiError && apiError.includes('source')) {
                        this.validationErrors = {
                            ...this.validationErrors,
                            source: apiError,
                        }
                    }

                    if (apiError && apiError.includes('deployment_name')) {
                        this.validationErrors = {
                            ...this.validationErrors,
                            deployment_name: apiError,
                        }
                    }
                } finally {
                    this.isSaving = false
                }
            },
        },
    }
</script>

<style scoped>
    .flow-config-page {
        min-height: calc(100vh - 80px);
    }

    .page-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
    }

    .page-title {
        margin: 0;
        font-size: 1.9rem;
        font-weight: 700;
        color: var(--color-text-primary);
    }

    .legend-trigger {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 1px;
        margin-bottom: 1px;
        color: var(--color-text-inverse);
        cursor: help;
        background-color: var(--color-brand-primary);
        font-size: 13px;
        padding: 0 10px;
        border-top-left-radius: 7px;
        border-top-right-radius: 7px;
    }

    .legend-trigger-wrapper {
        margin-left: 4px;
        outline: none;
    }

    .legend-popover {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(50%, -6px);
        min-width: 220px;
        background: var(--color-surface-canvas);
        border-radius: 10px;
        box-shadow: var(--shadow-lg);
        padding: 0.75rem;
        font-size: 0.75rem;
        color: var(--color-text-primary);
        opacity: 0;
        pointer-events: none;
        transition:
            opacity 0.18s ease,
            transform 0.18s ease;
        z-index: 20;
    }

    .legend-trigger:hover .legend-popover,
    .legend-trigger:focus-within .legend-popover {
        opacity: 1;
        pointer-events: auto;
        transform: translate(100%, -5px);
    }

    .legend-title {
        margin: 0 0 0.75rem;
        font-size: 0.82rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-primary);
    }

    .legend-popover ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.65rem;
    }

    .legend-popover li {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        color: var(--color-text-secondary);
        font-size: 0.9rem;
    }

    .table-card {
        background: var(--color-surface-canvas);
        border: 1px solid var(--color-border-default);
        box-shadow: var(--shadow-lg);
        overflow: hidden;
    }

    .vendor-table thead th {
        background: var(--color-surface-inverse);
        border-bottom: 1px solid var(--color-border-default);
        color: var(--color-text-inverse);
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 1rem;
    }

    .vendor-table tbody td {
        padding: 1rem;
        border-color: var(--color-border-muted);
        vertical-align: middle;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .vendor-table tbody tr:hover {
        background: var(--color-surface-subtle);
    }

    .vendor-table tbody tr:hover td {
        background: var(--color-surface-subtle);
    }

    .action-button {
        min-width: 92px;
    }

    .empty-state {
        padding: 4rem 1.5rem;
        text-align: center;
    }

    .details-backdrop {
        position: fixed;
        inset: 0;
        background: var(--color-surface-overlay);
        z-index: 1040;
    }

    .details-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        width: 400px;
        max-width: 92vw;
        height: 100vh;
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-lg);
        z-index: 1050;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        color: var(--color-text-primary);
    }

    .details-sidebar__header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.5rem;
        gap: 1rem;
    }

    .details-sidebar__eyebrow {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin: 0 0 0.25rem;
        color: var(--color-text-muted);
    }

    .details-sidebar__title {
        margin: 0;
        font-size: 1.75rem;
        font-weight: 600;
    }

    .details-sidebar__close {
        border: none;
        background: none;
        font-size: 1.75rem;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        color: var(--color-text-secondary);
    }

    .details-sidebar__content {
        flex: 1;
    }

    .details-sidebar__badges {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
        margin-top: 0.75rem;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.2rem 0.7rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: var(--color-text-primary);
        background: var(--color-neutral-soft);
    }

    .status-pill--active {
        color: var(--color-state-success);
        background: rgba(16, 185, 129, 0.22);
    }

    .status-pill--inactive {
        color: var(--color-text-secondary);
        background: var(--color-neutral-soft);
    }

    .status-pill--neutral {
        color: var(--color-text-primary);
        background: var(--color-neutral-soft);
    }

    .mode-indicator {
        width: 2rem;
        height: 2rem;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.95rem;
    }

    .mode-indicator--deployment {
        color: var(--color-state-success);
        background: rgba(16, 185, 129, 0.22);
    }

    .mode-indicator--direct {
        color: var(--color-text-muted);
        background: var(--color-neutral-soft);
    }

    .field-error {
        margin-top: 0.35rem;
        color: var(--color-brand-primary);
        font-size: 0.85rem;
    }

    .details-slide-enter-active,
    .details-slide-leave-active {
        transition: transform 0.25s ease;
    }

    .details-slide-enter-from,
    .details-slide-leave-to {
        transform: translateX(100%);
    }

    .details-fade-enter-active,
    .details-fade-leave-active {
        transition: opacity 0.25s ease;
    }

    .details-fade-enter-from,
    .details-fade-leave-to {
        opacity: 0;
    }

    @media (max-width: 768px) {
        .flow-config-page {
            padding-bottom: 1rem;
        }

        .vendor-table thead th,
        .vendor-table tbody td {
            padding: 0.8rem;
        }
    }
</style>
